import { RecordFields, RecordItem, RecordList } from "@/components/common/RecordList";
import { RecordSortControl } from "@/components/common/RecordSortControl";
import {
  AccessTimeFilled as StaleUpdateIcon,
  BatteryAlert,
  CheckCircle as HealthyIcon,
  Clear as ClearIcon,
  CloseFullscreen,
  ControlCamera,
  Devices as DevicesIcon,
  Error as CustomerWarningIcon,
  HideSource,
  Language,
  MedicalServices as HealthColumnIcon,
  OpenInFull,
  People as CustomersIcon,
  Person as AssociateActiveIcon,
  PersonOff as NoAssociateActivityIcon,
  Refresh as RefreshIcon,
  ShowChart,
  SignalWifiOff as OfflineIcon,
  SignalWifiStatusbar4Bar as AssociateWifiIcon,
  Store as StoreIcon,
  Visibility as ViewIcon,
  Warning as ToWatchIcon,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControlLabel,
  GridLegacy as Grid,
  IconButton,
  Skeleton,
  Stack,
  Switch,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import type { TFunction } from "i18next";
import { useSnackbar } from "notistack";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  AdminDialog,
  AdminEmptyState,
  AdminFilterPanel,
  AdminSectionPanel,
  AdminTableActions,
  AdminTableFrame,
  AdminTableLoadingRow as AdminLoadingRow,
  AdminTablePanel,
  PageShell,
} from "@/components/common";
import { DeviceBatteryChip } from "@/components/common/DeviceBatteryChip";
import { LoadingRing } from "@/components/common/LoadingRing";
import MuiAutocompleteMultiSelect from "@/components/common/MuiAutocompleteMultiSelect";
import { SortableTableCell } from "@/components/common/SortableTableCell";
import { TableSkeletonRows } from "@/components/common/TableSkeletonRows";
import type { StoreBranding } from "@/config/storeBranding";
import { useAuth } from "@/contexts/AuthContext";
import { useStoreBranding } from "@/hooks/useStoreBranding";
import { useTableSorting } from "@/hooks/useTableSorting";
import { paths } from "@/routes/paths";
import { Device } from "@/services/features/DeviceService";
import { Customer, Store } from "@/services/features/StoreService";
import { getDeviceBatteryDisplay } from "@/utils/deviceBattery";
import { getCurrentRegion, Region } from "@/utils/environment";
import { formatDecimal, formatInteger } from "@/utils/number";
import { formatStoreSelectorLabel, shouldUseLegacyCustomerStoreLabel } from "@/utils/store";
import {
  formatRelativeStoreTime,
  formatStoreDateTimeWithTimezone,
  getBrowserTimezone,
} from "@/utils/storeTime";

import { DeviceHealth, DeviceKind, StoreHealth, StoreHealthCache } from "./commandCenter.types";
import {
  type AssociateActivity,
  type CustomerDeviceTableRow,
  type CustomerHealthSummary,
  type DeviceCount,
  equalCustomerHealthRows,
  getCustomerHealthRows,
  getDeviceStatus,
  getMonitoringHealthStatus,
  isEligibleDevice,
  type MonitoringHealthStatus,
} from "./commandCenterHealth";
import { CommandCenterMap } from "./CommandCenterMap";
import { useCommandCenterData } from "./useCommandCenterData";

const kinds = ["SHOPPER", "DOOR", "ASSIST", "ASSOCIATE", "SURFACE", "CENTRAL"] as const;
const deviceTypeConfig: Record<DeviceKind, { labelKey: string; iconSrc: string }> = {
  SHOPPER: {
    labelKey: "commandCenter.columns.shopperDevices",
    iconSrc: "/images/app-icons/shopper_assist.svg",
  },
  DOOR: {
    labelKey: "commandCenter.columns.doorDevices",
    iconSrc: "/images/app-icons/door_central.svg",
  },
  ASSIST: {
    labelKey: "commandCenter.deviceTypes.assist",
    iconSrc: "/images/app-icons/shopper_assist.svg",
  },
  ASSOCIATE: {
    labelKey: "commandCenter.columns.associateDevices",
    iconSrc: "/images/app-icons/associate.svg",
  },
  SURFACE: {
    labelKey: "commandCenter.deviceTypes.surface",
    iconSrc: "/images/app-icons/surface.svg",
  },
  CENTRAL: {
    labelKey: "commandCenter.deviceTypes.central",
    iconSrc: "/images/app-icons/door_central.svg",
  },
};
const naCraveCustomerIds = new Set([12, 16, 38]);
type DeviceTableCountColumn = "shopper" | "assist" | "door" | "associate";
type CustomerDeviceTableColumn = "customer" | "customerHealth" | DeviceTableCountColumn | "health";
type StoreDeviceTableColumn = "customer" | DeviceTableCountColumn | "health" | "storeHealth";
type StoreDeviceTableRow = {
  store: Store;
  health?: StoreHealth;
  healthSummary: CustomerHealthSummary;
  healthStatus: MonitoringHealthStatus;
  associateActivity: AssociateActivity;
  issueRank: number;
  loading: boolean;
};
type StoreDeviceDetailTableColumn =
  "deviceId" | "deviceType" | "name" | "battery" | "lastUpdate" | "status";
type StoreDeviceDetailTableRow = {
  device: Device;
  status: ReturnType<typeof getDeviceStatus>;
  issueRank: number;
};
type ExcludedDeviceRow = { device: Device; store: Store };
const healthFor = (health?: StoreHealth) =>
  health?.total ?? { total: 0, online: 0, warning: 0, offline: 0, escalated: 0, lowBattery: 0 };
const emptyDeviceHealth = (): DeviceHealth => ({
  total: 0,
  online: 0,
  warning: 0,
  offline: 0,
  escalated: 0,
  lowBattery: 0,
});
const aggregateStoreHealth = (items: StoreHealth[]): StoreHealth => {
  const health: StoreHealth = {
    byType: Object.fromEntries(
      kinds.map((kind) => [kind, emptyDeviceHealth()]),
    ) as StoreHealth["byType"],
    total: emptyDeviceHealth(),
    hasRecentAssociateActivity: false,
  };

  for (const item of items) {
    for (const kind of kinds) {
      for (const metric of Object.keys(health.byType[kind]) as (keyof DeviceHealth)[]) {
        health.byType[kind][metric] += item.byType[kind]?.[metric] ?? 0;
      }
    }
    for (const metric of Object.keys(health.total) as (keyof DeviceHealth)[]) {
      health.total[metric] += item.total[metric];
    }
    health.hasRecentAssociateActivity ||= item.hasRecentAssociateActivity;
  }

  return health;
};
const emptyDeviceCount = (): DeviceCount => ({
  total: 0,
  online: 0,
  warning: 0,
  offline: 0,
  lowBattery: 0,
});
const addDeviceCount = (target: DeviceCount, health?: StoreHealth["total"]) => {
  target.total += health?.total ?? 0;
  target.online += health?.online ?? 0;
  target.warning += health?.warning ?? 0;
  target.offline += (health?.offline ?? 0) + (health?.escalated ?? 0);
  target.lowBattery += health?.lowBattery ?? 0;
};
const miscellaneousDeviceCount = (health?: StoreHealth): DeviceCount => {
  const count = emptyDeviceCount();
  addDeviceCount(count, health?.byType.ASSIST);
  addDeviceCount(count, health?.byType.CENTRAL);
  addDeviceCount(count, health?.byType.SURFACE);
  return count;
};
const miscellaneousColumnLabel = (t: TFunction) => (
  <Tooltip
    title={
      <Stack spacing={0}>
        <span>{t("commandCenter.deviceTypes.central")}</span>
        <span>{t("commandCenter.deviceTypes.surface")}</span>
      </Stack>
    }
  >
    <Box component="span">{t("commandCenter.columns.assistDevices")}</Box>
  </Tooltip>
);
const healthSummaryExcludingAssociateIssues = (health?: StoreHealth): CustomerHealthSummary => {
  const total = healthFor(health);
  const associate = health?.byType.ASSOCIATE;
  const warning = total.warning - (associate?.warning ?? 0);
  const offline =
    total.offline + total.escalated - (associate?.offline ?? 0) - (associate?.escalated ?? 0);
  const lowBattery = total.lowBattery;

  return {
    online: total.online,
    warning,
    offline,
    lowBattery,
    issueCount: warning + offline + lowBattery,
  };
};
const getStoreHealthStatus = (cache: StoreHealthCache | undefined): MonitoringHealthStatus =>
  cache?.state === "error" ? "error" : getMonitoringHealthStatus(cache?.health);
const isNaCraveCustomer = (customerId: number) =>
  getCurrentRegion() === Region.NA && naCraveCustomerIds.has(customerId);

const decodeHtmlEntities = (value: string) => {
  if (typeof document === "undefined" || !value.includes("&")) return value;

  const textarea = document.createElement("textarea");
  textarea.innerHTML = value;
  return textarea.value;
};

const iconOnlyTableCellSx = {
  width: "4.5em",
  minWidth: "4.5em",
  maxWidth: "4.5em",
  whiteSpace: "nowrap",
  textAlign: "center",
  px: 1,
  "& .MuiTableSortLabel-root": {
    justifyContent: "center",
    width: "100%",
  },
};
const iconOnlyTableHeaderCellSx = {};
const healthIconTableCellSx = {
  width: "5.5em",
  minWidth: "5.5em",
  maxWidth: "5.5em",
};
const healthIconTableHeaderCellSx = {
  textAlign: "center",
  "& .MuiTableSortLabel-root": {
    display: "flex",
    marginInline: "auto",
    width: "fit-content",
  },
};

const summaryHeaderSx: SxProps<Theme> = {
  backgroundColor: (theme) => (theme.palette.mode === "dark" ? "#374151" : "#F3F4F6"),
  borderTop: "1px solid",
  borderBottom: "1px solid",
  borderInlineStart: "1px solid",
  borderInlineEnd: "1px solid",
  borderColor: "divider",
  borderStartStartRadius: "12px",
  borderStartEndRadius: "12px",
  px: 2,
  py: 1.5,
};

const SummaryBody: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <CardContent
    sx={{
      display: "flex",
      flexDirection: "column",
      flex: 1,
      minHeight: 0,
      overflow: "auto",
      p: 2,
      "&:last-child": { pb: 2 },
    }}
  >
    {children}
  </CardContent>
);

const CommandCenterSummarySkeleton: React.FC = () => (
  <Card sx={{ height: 400, display: "flex", flexDirection: "column", overflow: "hidden" }}>
    <Box sx={summaryHeaderSx}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" spacing={1} alignItems="center">
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton variant="text" width={176} height={28} />
        </Stack>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Skeleton variant="text" width={36} />
          <Skeleton variant="text" width={36} />
        </Stack>
      </Stack>
    </Box>
    <SummaryBody>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1fr)",
          gap: 1,
        }}
      >
        {Array.from({ length: 3 }, (_, index) => (
          <Skeleton key={index} variant="rounded" height={96} />
        ))}
      </Box>
    </SummaryBody>
  </Card>
);

const CommandCenterMapSkeleton: React.FC = () => (
  <Card sx={{ height: 400, overflow: "hidden" }}>
    <Skeleton variant="rectangular" width="100%" height="100%" />
  </Card>
);

const CommandCenterTableSkeleton: React.FC = () => (
  <AdminTableFrame>
    <TableHead>
      <TableRow>
        {Array.from({ length: 6 }, (_, index) => (
          <TableCell key={index}>
            <Skeleton variant="text" width="70%" />
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
    <TableBody>
      <TableSkeletonRows columnCount={6} />
    </TableBody>
  </AdminTableFrame>
);

const StoreInformation = ({ store, t, locale }: { store: Store; t: TFunction; locale: string }) => {
  const navigate = useNavigate();
  const address = [store.street, store.city, store.state, store.zip].filter(Boolean).join(", ");
  const latitude = store.geoPoint?.lat;
  const longitude = store.geoPoint?.lon;
  const coordinates =
    typeof latitude === "number" &&
    typeof longitude === "number" &&
    Number.isFinite(latitude) &&
    Number.isFinite(longitude)
      ? `${formatDecimal(latitude, locale, { maximumFractionDigits: 6 })}, ${formatDecimal(
          longitude,
          locale,
          { maximumFractionDigits: 6 },
        )}`
      : t("commandCenter.notAvailable");
  const notAvailable = t("commandCenter.notAvailable");
  const details = [
    { label: t("commandCenter.columns.name"), value: store.name },
    { label: t("commandCenter.fields.storeId"), value: formatInteger(store.id, locale) },
    { label: t("commandCenter.fields.externalIdLabel"), value: store.externalId || notAvailable },
    { label: t("commandCenter.fields.address"), value: address || notAvailable },
    { label: t("commandCenter.fields.coordinates"), value: coordinates },
    { label: t("commandCenter.fields.phoneNumber"), value: store.phoneNumber || notAvailable },
    { label: t("commandCenter.fields.group"), value: store.group || notAvailable },
    { label: t("commandCenter.fields.timezone"), value: store.timezone || notAvailable },
    {
      label: t("commandCenter.fields.currency"),
      value: store.currencyCode?.trim() || store.currencySymbol || notAvailable,
    },
    ...(store.locale?.trim()
      ? [{ label: t("commandCenter.fields.locale"), value: store.locale }]
      : []),
  ];

  return (
    <AdminSectionPanel
      paperSx={{ mb: 3 }}
      title={t("commandCenter.sections.storeInformation")}
      icon={<StoreIcon />}
      actions={
        <Button
          variant="outlined"
          color="primary"
          startIcon={<ShowChart />}
          onClick={() => void navigate(`${paths.storeInsights.performance}?storeId=${store.id}`)}
        >
          {t("storeInsights.storePerformance.title")}
        </Button>
      }
    >
      <Grid container spacing={2}>
        {details.map(({ label, value }) => (
          <Grid key={label} item xs={12} sm={6} lg={4}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 700,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
              }}
            >
              {label}
            </Typography>
            <Typography sx={{ overflowWrap: "anywhere" }}>{value}</Typography>
          </Grid>
        ))}
      </Grid>
    </AdminSectionPanel>
  );
};

const CommandCenterPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const locale = i18n.resolvedLanguage || i18n.language || "en";
  const [params, setParams] = useSearchParams();
  const [isMapExpanded, setIsMapExpanded] = React.useState(false);
  const scrollPositionsByScope = React.useRef(new Map<string, number>());
  const handleStoreDeviceHealthLoadError = React.useCallback(
    (store: Store) => {
      enqueueSnackbar(
        t("commandCenter.errors.failedLoadStoreDeviceHealth", {
          storeId: formatInteger(store.id, locale),
          storeName: decodeHtmlEntities(store.name),
        }),
        { variant: "error" },
      );
    },
    [enqueueSnackbar, locale, t],
  );
  const customerIdParam = Number(params.get("customerId"));
  const storeIdParam = Number(params.get("storeId"));
  const {
    customers,
    stores,
    healthByStoreId,
    isInitialLoading,
    isRefreshing,
    initialError,
    refresh,
    prioritizeCustomer,
  } = useCommandCenterData({
    priorityCustomerId: Number.isFinite(customerIdParam) ? customerIdParam : undefined,
    priorityStoreId: Number.isFinite(storeIdParam) ? storeIdParam : undefined,
    onStoreDeviceHealthLoadError: handleStoreDeviceHealthLoadError,
  });
  const selectedStore = stores.find((store) => store.id === storeIdParam) ?? null;
  const selectedCustomer = customers.find((customer) => customer.id === customerIdParam) ?? null;
  const scrollScope = selectedStore
    ? `store:${selectedStore.id}`
    : selectedCustomer
      ? `customer:${selectedCustomer.id}`
      : "portfolio";

  React.useEffect(() => {
    if (isInitialLoading) return;

    const frame = window.requestAnimationFrame(() => {
      window.scrollTo({ top: scrollPositionsByScope.current.get(scrollScope) ?? 0 });
    });

    return () => {
      window.cancelAnimationFrame(frame);
      scrollPositionsByScope.current.set(scrollScope, window.scrollY);
    };
  }, [isInitialLoading, scrollScope]);

  React.useEffect(() => {
    if (isInitialLoading) return;
    const next = new URLSearchParams();
    if (selectedCustomer) next.set("customerId", String(selectedCustomer.id));
    if (selectedStore) next.set("storeId", String(selectedStore.id));
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
  }, [isInitialLoading, params, selectedCustomer, selectedStore, setParams]);
  const setSelection = React.useCallback(
    (customer: Customer | null, store: Store | null) => {
      prioritizeCustomer(customer?.id);
      const next = new URLSearchParams();
      if (customer) next.set("customerId", String(customer.id));
      if (store) next.set("storeId", String(store.id));
      setParams(next);
    },
    [prioritizeCustomer, setParams],
  );
  const getStoreOptionLabel = React.useCallback(
    (store: Store) => {
      const name = decodeHtmlEntities(store.name);

      return shouldUseLegacyCustomerStoreLabel(user?.role)
        ? t("commandCenter.filters.storeOption", {
            id: formatInteger(store.id, locale),
            name,
          })
        : formatStoreSelectorLabel({ ...store, name }, t, user?.role);
    },
    [locale, t, user?.role],
  );
  const handleStoreSelect = React.useCallback(
    (store: Store | null) => {
      if (!store) {
        setSelection(selectedCustomer, null);
        return;
      }

      setSelection(selectedCustomer, store);
    },
    [selectedCustomer, setSelection],
  );
  const scopedStores = React.useMemo(
    () =>
      stores
        .filter((store) => !selectedCustomer || store.customerId === selectedCustomer.id)
        .filter((store) => !selectedStore || store.id === selectedStore.id),
    [stores, selectedCustomer?.id, selectedStore?.id],
  );
  const excludedDevices = React.useMemo<ExcludedDeviceRow[]>(
    () =>
      scopedStores
        .flatMap((store) =>
          (healthByStoreId[store.id]?.ignoredDevices ?? []).map((device) => ({ device, store })),
        )
        .sort((first, second) => first.device.id - second.device.id),
    [healthByStoreId, scopedStores],
  );
  const mapStores = React.useMemo(
    () => stores.filter((store) => !selectedCustomer || store.customerId === selectedCustomer.id),
    [stores, selectedCustomer?.id],
  );
  const scopedCustomers = React.useMemo(
    () => customers.filter((customer) => stores.some((store) => store.customerId === customer.id)),
    [customers, stores],
  );
  const customerRows = React.useMemo(
    () => getCustomerHealthRows(scopedCustomers, stores, healthByStoreId),
    [scopedCustomers, stores, healthByStoreId],
  );
  const handleCustomerSelect = React.useCallback(
    (customer: Customer) => setSelection(customer, null),
    [setSelection],
  );
  const storeSelectorOptions = React.useMemo(
    () =>
      stores
        .filter((store) => !selectedCustomer || store.customerId === selectedCustomer.id)
        .sort((first, second) => first.id - second.id),
    [selectedCustomer?.id, stores],
  );
  const handleMapStoreSelect = React.useCallback(
    (storeId: number) => {
      const store = stores.find((item) => item.id === storeId);
      if (store) handleStoreSelect(store);
    },
    [handleStoreSelect, stores],
  );
  const selectedCache = selectedStore ? healthByStoreId[selectedStore.id] : undefined;
  const selectedHealth = selectedCache?.health;
  const customerHealth = (customerId: number) =>
    stores
      .filter((store) => store.customerId === customerId)
      .map((store) => healthByStoreId[store.id]?.health)
      .filter(Boolean) as StoreHealth[];
  const summaryCard = (
    <Card
      sx={{
        height: { md: 400 },
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {selectedStore ? (
        <StoreSummary
          store={selectedStore}
          health={selectedHealth}
          loading={
            !selectedCache || selectedCache.state === "queued" || selectedCache.state === "loading"
          }
          t={t}
          locale={locale}
        />
      ) : selectedCustomer ? (
        <CustomerSummary
          customer={selectedCustomer}
          stores={scopedStores}
          health={aggregateStoreHealth(customerHealth(selectedCustomer.id))}
          loading={scopedStores.some((store) => {
            const state = healthByStoreId[store.id]?.state;
            return !state || state === "queued" || state === "loading";
          })}
          t={t}
          locale={locale}
        />
      ) : (
        <PortfolioOverview
          rows={customerRows}
          storeCount={stores.length}
          onSelect={handleCustomerSelect}
          t={t}
          locale={locale}
        />
      )}
    </Card>
  );
  return (
    <PageShell
      breadcrumbs="admin"
      title={t("commandCenter.title")}
      icon={<ControlCamera />}
      titleAccessory={<LoadingRing loading={isRefreshing} />}
      headerActions={
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => void refresh({ force: true })}
          disabled={isRefreshing}
        >
          {t("commandCenter.actions.refresh")}
        </Button>
      }
    >
      <AdminFilterPanel sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md>
            <MuiAutocompleteMultiSelect
              options={scopedCustomers}
              value={selectedCustomer ? [selectedCustomer] : []}
              getOptionLabel={(option) => option.name}
              getOptionKey={(option) => option.id}
              loading={isInitialLoading}
              label={t("commandCenter.filters.selectCustomer")}
              onChange={(next) => setSelection(next[0] ?? null, null)}
            />
          </Grid>
          <Grid item xs={12} md>
            <MuiAutocompleteMultiSelect
              options={storeSelectorOptions}
              value={selectedStore ? [selectedStore] : []}
              loading={isInitialLoading}
              getOptionLabel={getStoreOptionLabel}
              getOptionKey={(option) => option.id}
              label={t("commandCenter.filters.selectStore")}
              onChange={(next) => handleStoreSelect(next[0] ?? null)}
            />
          </Grid>
          <Grid item xs={12} md="auto" sx={{ alignSelf: "center" }}>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={() => setSelection(null, null)}
              disabled={!selectedCustomer && !selectedStore}
              color="primary"
              size="medium"
              sx={{ width: { xs: "100%", md: "auto" } }}
            >
              {t("common.clearFilters")}
            </Button>
          </Grid>
        </Grid>
      </AdminFilterPanel>
      {initialError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {initialError || t("commandCenter.errors.failedFetchInitialData")}
        </Alert>
      )}
      {isInitialLoading ? (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} lg={6} sx={{ order: { xs: 2, lg: 1 } }}>
              <CommandCenterSummarySkeleton />
            </Grid>
            <Grid item xs={12} lg={6} sx={{ order: { xs: 1, lg: 2 } }}>
              <CommandCenterMapSkeleton />
            </Grid>
          </Grid>
          <CommandCenterTableSkeleton />
        </>
      ) : (
        <>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {!isMapExpanded && (
              <Grid item xs={12} lg={6} sx={{ order: { xs: 2, lg: 1 } }}>
                {summaryCard}
              </Grid>
            )}
            <Grid
              item
              xs={12}
              lg={isMapExpanded ? 12 : 6}
              sx={{ order: { xs: 1, lg: isMapExpanded ? 1 : 2 } }}
            >
              <Card sx={{ position: "relative" }}>
                <Tooltip
                  title={
                    isMapExpanded
                      ? t("commandCenter.actions.collapseMap")
                      : t("commandCenter.actions.expandMap")
                  }
                >
                  <IconButton
                    aria-label={
                      isMapExpanded
                        ? t("commandCenter.actions.collapseMap")
                        : t("commandCenter.actions.expandMap")
                    }
                    onClick={() => setIsMapExpanded((expanded) => !expanded)}
                    sx={{
                      position: "absolute",
                      insetBlockStart: 8,
                      insetInlineStart: 8,
                      zIndex: 1,
                      bgcolor: "background.paper",
                      boxShadow: 1,
                      "&:hover": { bgcolor: "action.hover" },
                      display: { xs: "none", lg: "inline-flex" },
                    }}
                  >
                    {isMapExpanded ? (
                      <CloseFullscreen sx={{ fontSize: 18 }} />
                    ) : (
                      <OpenInFull sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                </Tooltip>
                <CommandCenterMap
                  customers={customers}
                  stores={mapStores}
                  selectedCustomerId={selectedCustomer?.id ?? null}
                  selectedStoreId={selectedStore?.id ?? null}
                  healthByStoreId={healthByStoreId}
                  onSelectStore={handleMapStoreSelect}
                  layoutVersion={Number(isMapExpanded)}
                  expanded={isMapExpanded}
                />
              </Card>
            </Grid>
            {isMapExpanded && (
              <Grid item xs={12} sx={{ order: { xs: 2, lg: 2 } }}>
                {summaryCard}
              </Grid>
            )}
          </Grid>
          {selectedStore && <StoreInformation store={selectedStore} t={t} locale={locale} />}
          {selectedStore ? (
            <StoreDeviceDetailTable
              devices={selectedCache?.devices ?? []}
              store={selectedStore}
              error={selectedCache?.state === "error"}
              loading={
                !selectedCache ||
                selectedCache.state === "queued" ||
                selectedCache.state === "loading"
              }
              t={t}
              locale={locale}
              excludedDevices={excludedDevices}
            />
          ) : selectedCustomer ? (
            <StoreDeviceTable
              stores={scopedStores}
              healthByStoreId={healthByStoreId}
              t={t}
              locale={locale}
              showStoreHealth
              onSelectStore={handleStoreSelect}
              excludedDevices={excludedDevices}
            />
          ) : (
            <CustomerDeviceTable
              rows={customerRows}
              t={t}
              locale={locale}
              onSelectCustomer={handleCustomerSelect}
              excludedDevices={excludedDevices}
            />
          )}
        </>
      )}
    </PageShell>
  );
};

type CustomerRowProps = {
  row: CustomerDeviceTableRow;
  branding: StoreBranding;
  t: TFunction;
  locale: string;
  onSelect: (customer: Customer) => void;
};
const equalCustomerRowProps = (a: CustomerRowProps, b: CustomerRowProps) =>
  a.t === b.t &&
  a.locale === b.locale &&
  a.onSelect === b.onSelect &&
  a.branding.avatar === b.branding.avatar &&
  a.branding.color === b.branding.color &&
  equalCustomerHealthRows(a.row, b.row);

const PortfolioCustomerCard = React.memo(function PortfolioCustomerCard({
  row,
  branding,
  onSelect,
  t,
  locale,
}: CustomerRowProps) {
  const customer = row.customerRecord;
  const isLoading = row.loading;
  const healthStatus = isLoading ? "neutral" : row.healthStatus;
  return (
    <ButtonBase
      onClick={() => onSelect(customer)}
      sx={{
        minWidth: 0,
        minHeight: 96,
        p: 1.5,
        bgcolor: "background.paper",
        borderRadius: 1,
        border: "1px solid",
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        textAlign: "start",
        transition: "background-color 0.2s",
        "&:hover": {
          bgcolor: "action.hover",
        },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
        <Avatar
          src={branding.avatar}
          sx={{
            width: 48,
            height: 48,
            flexShrink: 0,
            border: `2px solid ${branding.color}`,
            boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
          }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" fontWeight="bold" noWrap>
            {customer.name}
          </Typography>
        </Box>
        {healthStatus === "healthy" && (
          <Tooltip title={t("commandCenter.status.healthy")}>
            <HealthyIcon color="success" fontSize="small" />
          </Tooltip>
        )}
        {healthStatus === "toWatch" && (
          <Tooltip title={t("commandCenter.status.customerHasStoreToWatch")}>
            <ToWatchIcon color="warning" fontSize="small" />
          </Tooltip>
        )}
        {healthStatus === "unhealthy" && (
          <Tooltip title={t("commandCenter.status.customerHasUnhealthyStore")}>
            <CustomerWarningIcon color="error" fontSize="small" />
          </Tooltip>
        )}
        {healthStatus === "error" && (
          <Tooltip title={t("commandCenter.errors.errorLoadingDevices")}>
            <CustomerWarningIcon color="error" fontSize="small" />
          </Tooltip>
        )}
      </Stack>
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mt: 1.5 }}>
        <Tooltip title={t("commandCenter.metrics.storeCount", { count: row.storeCount })}>
          <Chip size="small" icon={<StoreIcon />} label={formatInteger(row.storeCount, locale)} />
        </Tooltip>
        {isLoading ? (
          <>
            <Skeleton variant="text" width={64} />
            <Skeleton variant="text" width={58} />
          </>
        ) : (
          <>
            {row.health.warning > 0 && (
              <Tooltip
                title={t("commandCenter.metrics.warningCount", { count: row.health.warning })}
              >
                <Chip
                  size="small"
                  color="warning"
                  icon={<StaleUpdateIcon />}
                  label={formatInteger(row.health.warning, locale)}
                />
              </Tooltip>
            )}
            {row.health.offline > 0 && (
              <Tooltip
                title={t("commandCenter.metrics.offlineCount", { count: row.health.offline })}
              >
                <Chip
                  size="small"
                  color="error"
                  icon={<OfflineIcon sx={{ color: "error.main" }} />}
                  label={formatInteger(row.health.offline, locale)}
                />
              </Tooltip>
            )}
          </>
        )}
      </Stack>
    </ButtonBase>
  );
}, equalCustomerRowProps);

const PortfolioOverview = ({
  rows,
  storeCount,
  onSelect,
  t,
  locale,
}: {
  rows: CustomerDeviceTableRow[];
  storeCount: number;
  onSelect: (customer: Customer) => void;
  t: TFunction;
  locale: string;
}) => {
  const { getStoreBranding } = useStoreBranding();
  return (
    <>
      <Box sx={summaryHeaderSx}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1} alignItems="center">
            <Language />
            <Typography variant="h6">{t("commandCenter.sections.globalStoreMetrics")}</Typography>
          </Stack>
          <Stack direction="row" spacing={1.5} alignItems="center" color="text.primary">
            <Stack direction="row" spacing={0.5} alignItems="center">
              <CustomersIcon fontSize="small" />
              <Typography color="inherit">{formatInteger(rows.length, locale)}</Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <StoreIcon fontSize="small" />
              <Typography color="inherit">{formatInteger(storeCount, locale)}</Typography>
            </Stack>
          </Stack>
        </Stack>
      </Box>
      <SummaryBody>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" },
            gridAutoRows: "auto",
            gap: 1,
            alignContent: "start",
          }}
        >
          {[...rows]
            .sort(
              (a, b) =>
                Number(isNaCraveCustomer(a.customerId)) - Number(isNaCraveCustomer(b.customerId)),
            )
            .map((row, index, orderedRows) => {
              const customer = row.customerRecord;
              const startsCraveCustomers =
                isNaCraveCustomer(customer.id) &&
                (!orderedRows[index - 1] || !isNaCraveCustomer(orderedRows[index - 1].customerId));
              return (
                <React.Fragment key={customer.id}>
                  {startsCraveCustomers && (
                    <Divider sx={{ gridColumn: "1 / -1", borderColor: "divider", my: 1 }} />
                  )}
                  <PortfolioCustomerCard
                    row={row}
                    branding={getStoreBranding(customer.id)}
                    onSelect={onSelect}
                    t={t}
                    locale={locale}
                  />
                </React.Fragment>
              );
            })}
        </Box>
      </SummaryBody>
    </>
  );
};
const CustomerSummary = ({ customer, store, stores, health, loading, t, locale }: any) => {
  const { getStoreBranding } = useStoreBranding();
  const customerId = store?.customerId ?? customer.id;
  const title = store?.name ?? customer.name;
  const summaryCount = store ? (health?.total?.total ?? 0) : stores.length;
  const SummaryCountIcon = store ? DevicesIcon : StoreIcon;
  const deviceTypes = kinds.map((kind) => ({
    kind,
    label: t(deviceTypeConfig[kind].labelKey),
    iconSrc: deviceTypeConfig[kind].iconSrc,
  }));
  const deviceTypeImage = (src: string) => (
    <Box
      component="img"
      src={src}
      alt=""
      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
    />
  );
  const visibleDeviceTypes = deviceTypes.filter(
    ({ kind }) => (health.byType?.[kind]?.total ?? 0) > 0,
  );
  const hasNoDevices = !loading && visibleDeviceTypes.length === 0;
  return (
    <>
      <Stack direction="row" spacing={2} alignItems="center" sx={summaryHeaderSx}>
        <Avatar
          src={getStoreBranding(customerId).avatar}
          sx={{
            width: 56,
            height: 56,
            border: `2px solid ${getStoreBranding(customerId).color}`,
          }}
        />
        <Box>
          <Typography variant="h6">{title}</Typography>
          <Stack direction="row" spacing={0.5} alignItems="center" color="text.primary">
            <SummaryCountIcon fontSize="small" />
            <Typography variant="body2" color="inherit">
              {loading && store ? (
                <Skeleton variant="text" width={28} />
              ) : (
                formatInteger(summaryCount, locale)
              )}
            </Typography>
          </Stack>
        </Box>
      </Stack>
      <SummaryBody>
        <Box
          sx={{
            display: "grid",
            flex: 1,
            gridTemplateColumns: "minmax(0, 1fr)",
            gap: 1,
            alignContent: hasNoDevices ? "center" : "start",
            justifyItems: hasNoDevices ? "center" : "stretch",
          }}
        >
          {loading ? (
            Array.from({ length: 4 }, (_, index) => (
              <Box
                key={index}
                sx={{
                  minHeight: 96,
                  p: 1.5,
                  bgcolor: "background.paper",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Skeleton variant="circular" width={40} height={40} />
                  <Skeleton variant="text" width="45%" />
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                  <Skeleton variant="rounded" width={72} height={24} />
                  <Skeleton variant="rounded" width={64} height={24} />
                </Stack>
              </Box>
            ))
          ) : hasNoDevices ? (
            <AdminEmptyState
              icon={<DevicesIcon sx={{ fontSize: 40 }} />}
              text={t("commandCenter.empty.noDevicesFound", { message: "" })}
            />
          ) : (
            visibleDeviceTypes.map(({ kind, label, iconSrc }) => {
              const deviceHealth = health.byType?.[kind];
              const isAssociate = kind === "ASSOCIATE";
              const hasOnlineAssociateDevice = isAssociate && (deviceHealth?.online ?? 0) > 0;
              const warning = deviceHealth?.warning ?? 0;
              const offline = (deviceHealth?.offline ?? 0) + (deviceHealth?.escalated ?? 0);
              const lowBattery = deviceHealth?.lowBattery ?? 0;
              const hasIssues = warning > 0 || offline > 0;
              return (
                <Box
                  key={kind}
                  sx={{
                    minWidth: 0,
                    minHeight: 96,
                    p: 1.5,
                    bgcolor: "background.paper",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch",
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: "action.hover",
                        border: "1px solid",
                        borderColor: "divider",
                        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.12)",
                      }}
                    >
                      {deviceTypeImage(iconSrc)}
                    </Avatar>
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      noWrap
                      sx={{ minWidth: 0, flex: 1 }}
                    >
                      {label}
                    </Typography>
                    {!loading && isAssociate && (
                      <Tooltip
                        title={
                          hasOnlineAssociateDevice
                            ? t("commandCenter.status.active")
                            : t("commandCenter.status.noRecentActivity")
                        }
                      >
                        {hasOnlineAssociateDevice ? (
                          <AssociateWifiIcon color="primary" fontSize="small" />
                        ) : (
                          <OfflineIcon sx={{ color: "text.primary", fontSize: 20 }} />
                        )}
                      </Tooltip>
                    )}
                    {!loading &&
                      !isAssociate &&
                      (hasIssues ? (
                        <Tooltip
                          title={t("commandCenter.metrics.warningCount", {
                            count: warning + offline,
                          })}
                        >
                          <CustomerWarningIcon color="warning" fontSize="small" />
                        </Tooltip>
                      ) : (
                        <Tooltip title={t("commandCenter.status.healthy")}>
                          <HealthyIcon color="success" fontSize="small" />
                        </Tooltip>
                      ))}
                  </Stack>
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mt: 1.5 }}>
                    <Chip
                      size="small"
                      icon={<DevicesIcon />}
                      label={t("commandCenter.metrics.totalCount", {
                        count: deviceHealth?.total ?? 0,
                      })}
                    />
                    {loading ? (
                      <>
                        <Skeleton variant="text" width={64} />
                        <Skeleton variant="text" width={58} />
                      </>
                    ) : (
                      <>
                        {lowBattery > 0 && (
                          <Chip
                            size="small"
                            color="warning"
                            icon={<BatteryAlert />}
                            label={t("commandCenter.status.lowBattery", { count: lowBattery })}
                          />
                        )}
                        {!isAssociate && warning > 0 && (
                          <Chip
                            size="small"
                            color="warning"
                            icon={<StaleUpdateIcon />}
                            label={t("commandCenter.status.staleUpdates", { count: warning })}
                          />
                        )}
                        {!isAssociate && offline > 0 && (
                          <Chip
                            size="small"
                            color="error"
                            icon={<OfflineIcon sx={{ color: "error.main" }} />}
                            label={t("commandCenter.metrics.offlineCount", { count: offline })}
                          />
                        )}
                      </>
                    )}
                  </Stack>
                </Box>
              );
            })
          )}
        </Box>
      </SummaryBody>
    </>
  );
};
const StoreSummary = ({ store, health, loading, t, locale }: any) => (
  <CustomerSummary
    store={store}
    stores={[]}
    health={health ?? aggregateStoreHealth([])}
    loading={loading}
    t={t}
    locale={locale}
  />
);
const ExcludedDevicesControl = ({
  excludedDevices,
  t,
}: {
  excludedDevices: ExcludedDeviceRow[];
  t: TFunction;
}) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = React.useState(false);
  const viewDevice = (deviceId: number) => navigate(`/admin/hardware/devices/history/${deviceId}`);

  return (
    <>
      <Button
        disabled={excludedDevices.length === 0}
        onClick={() => setIsOpen(true)}
        size="small"
        variant="text"
      >
        {t("commandCenter.actions.excludedDevices", { count: excludedDevices.length })}
      </Button>
      <AdminDialog
        cancelAction={{ label: t("common.close"), onClick: () => setIsOpen(false) }}
        icon={<HideSource />}
        maxWidth="sm"
        onClose={() => setIsOpen(false)}
        open={isOpen}
        title={t("commandCenter.dialogs.excludedDevicesTitle")}
      >
        <AdminTablePanel
          elevation={0}
          tableProps={{ size: "small" }}
          mobileContent={
            <Box
              sx={{
                minWidth: 0,
                "& .MuiTypography-root": {
                  maxWidth: "100%",
                  whiteSpace: "normal",
                  overflowWrap: "anywhere",
                },
              }}
            >
              <RecordList
                items={excludedDevices}
                getItemId={({ device }) => device.id}
                loading={false}
                resetKey={JSON.stringify([
                  null,
                  null,
                  ...excludedDevices.map(({ device }) => device.id),
                ])}
                renderTitle={({ device }) => {
                  return <>{device.id}</>;
                }}
                renderSummary={({ store }) => {
                  return (
                    <RecordFields
                      fields={[
                        {
                          label: <>{t("management.devices.devices.fields.store")}</>,
                          value: <>{store.name}</>,
                        },
                      ]}
                    />
                  );
                }}

                renderActions={({ device }) => (
                  <AdminTableActions
                    actions={[
                      {
                        action: () => viewDevice(device.id),
                        ariaLabel: t("commandCenter.actions.view"),
                        hint: t("commandCenter.actions.view"),
                        icon: <ViewIcon />,
                      },
                    ]}
                  />
                )}
                emptyState={<Box />}
              />
            </Box>
          }
        >
          <TableHead>
            <TableRow>
              <TableCell>{t("commandCenter.columns.deviceId")}</TableCell>
              <TableCell>{t("management.devices.devices.fields.store")}</TableCell>
              <TableCell sx={{ textAlign: "end" }}>{t("commandCenter.columns.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {excludedDevices.map(({ device, store }) => (
              <TableRow
                hover
                key={device.id}
                onClick={() => viewDevice(device.id)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    viewDevice(device.id);
                  }
                }}
                role="button"
                sx={{ cursor: "pointer" }}
                tabIndex={0}
              >
                <TableCell>{device.id}</TableCell>
                <TableCell>{store.name}</TableCell>
                <TableCell sx={{ textAlign: "end" }}>
                  <Box onClick={(event) => event.stopPropagation()}>
                    <AdminTableActions
                      actions={[
                        {
                          action: () => viewDevice(device.id),
                          ariaLabel: t("commandCenter.actions.view"),
                          hint: t("commandCenter.actions.view"),
                          icon: <ViewIcon />,
                        },
                      ]}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </AdminTablePanel>
      </AdminDialog>
    </>
  );
};
const StoreDeviceTable = ({
  stores,
  healthByStoreId,
  t,
  locale,
  showStoreHealth,
  onSelectStore,
  excludedDevices,
}: any) => {
  const [hideHealthyStores, setHideHealthyStores] = React.useState(false);
  const [hideStoresWithoutDevices, setHideStoresWithoutDevices] = React.useState(true);
  const rows = React.useMemo<StoreDeviceTableRow[]>(
    () =>
      stores.map((store: Store) => {
        const cache = healthByStoreId[store.id];
        const health = cache?.health;
        const healthSummary = healthSummaryExcludingAssociateIssues(health);
        const associateActivity: AssociateActivity =
          (health?.byType.ASSOCIATE.total ?? 0) === 0
            ? "none"
            : health?.hasRecentAssociateActivity
              ? "active"
              : "noRecent";

        return {
          store,
          health,
          healthSummary,
          healthStatus: getStoreHealthStatus(cache),
          associateActivity,
          issueRank: healthSummary.issueCount > 0 ? 0 : 1,
          loading: !cache || cache.state === "queued" || cache.state === "loading",
        };
      }),
    [healthByStoreId, stores],
  );
  const { sortColumn, sortDirection, handleSort, sortedItems } = useTableSorting<
    StoreDeviceTableRow,
    StoreDeviceTableColumn | "issueRank"
  >(
    rows,
    (row, column) => {
      if (column === "customer") return row.store.name;
      if (column === "health") return row.healthSummary.issueCount;
      if (column === "storeHealth") {
        return row.healthStatus === "error"
          ? 0
          : row.healthStatus === "unhealthy"
            ? 1
            : row.healthStatus === "toWatch"
              ? 2
              : row.healthStatus === "healthy"
                ? 3
                : 4;
      }
      if (column === "issueRank") return row.issueRank;

      if (column === "assist") return miscellaneousDeviceCount(row.health).total;

      const kind: DeviceKind =
        column === "shopper"
          ? "SHOPPER"
          : column === "door"
            ? "DOOR"
            : column === "associate"
              ? "ASSOCIATE"
              : "ASSIST";
      return row.health?.byType[kind]?.total ?? 0;
    },
    showStoreHealth ? "storeHealth" : "issueRank",
    locale,
  );
  const displayedItems = React.useMemo(
    () =>
      sortedItems.filter(
        (row) =>
          !(hideHealthyStores && row.healthStatus === "healthy") &&
          !(hideStoresWithoutDevices && !row.loading && healthFor(row.health).total === 0),
      ),
    [hideHealthyStores, hideStoresWithoutDevices, sortedItems],
  );
  const countFor = (deviceHealth?: StoreHealth["total"]): DeviceCount => {
    const count = emptyDeviceCount();
    addDeviceCount(count, deviceHealth);
    return count;
  };
  const columns: { key: StoreDeviceTableColumn; label: React.ReactNode }[] = [
    ...(showStoreHealth
      ? [
          {
            key: "storeHealth" as const,
            label: (
              <Tooltip title={t("commandCenter.columns.health")}>
                <HealthColumnIcon sx={{ fontSize: 18 }} />
              </Tooltip>
            ),
          },
        ]
      : []),
    { key: "customer", label: t("commandCenter.columns.customerStore") },
    { key: "health", label: t("commandCenter.columns.totalDevices") },
    { key: "shopper", label: t("commandCenter.columns.shopperDevices") },
    { key: "door", label: t("commandCenter.columns.doorDevices") },
    { key: "associate", label: t("commandCenter.columns.associateDevices") },
    { key: "assist", label: miscellaneousColumnLabel(t) },
  ];
  const trailingCellDividerSx = { borderInlineEnd: "1px solid", borderColor: "divider" };
  const healthCellSx = { ...trailingCellDividerSx, borderTop: 0, borderBottom: 0 };
  const storeHealthCellSx = {
    ...trailingCellDividerSx,
    borderTop: 0,
    borderBottom: 0,
    ...healthIconTableCellSx,
    ...healthIconTableHeaderCellSx,
  };
  const renderDeviceCount = (count: DeviceCount) => (
    <Stack spacing={0.5} alignItems="flex-start">
      {count.online > 0 && (
        <Chip
          size="small"
          color="success"
          icon={<HealthyIcon />}
          label={t("commandCenter.metrics.onlineCount", { count: count.online })}
        />
      )}
      {count.lowBattery > 0 && (
        <Chip
          size="small"
          color="warning"
          icon={<BatteryAlert />}
          label={t("commandCenter.status.lowBattery", { count: count.lowBattery })}
        />
      )}
      {count.warning > 0 && (
        <Chip
          size="small"
          color="warning"
          icon={<StaleUpdateIcon />}
          label={t("commandCenter.status.staleUpdates", { count: count.warning })}
        />
      )}
      {count.offline > 0 && (
        <Chip
          size="small"
          color="error"
          icon={<OfflineIcon sx={{ color: "error.main" }} />}
          label={t("commandCenter.metrics.offlineCount", { count: count.offline })}
        />
      )}
    </Stack>
  );
  const renderHealth = (summary: CustomerHealthSummary) => (
    <Stack spacing={0.5} alignItems="flex-start">
      {summary.online > 0 && (
        <Chip
          size="small"
          color="success"
          icon={<HealthyIcon />}
          label={t("commandCenter.metrics.onlineCount", { count: summary.online })}
        />
      )}
      {summary.lowBattery > 0 && (
        <Chip
          size="small"
          color="warning"
          icon={<BatteryAlert />}
          label={t("commandCenter.status.lowBattery", { count: summary.lowBattery })}
        />
      )}
      {summary.warning > 0 && (
        <Chip
          size="small"
          color="warning"
          icon={<StaleUpdateIcon />}
          label={t("commandCenter.status.staleUpdates", { count: summary.warning })}
        />
      )}
      {summary.offline > 0 && (
        <Chip
          size="small"
          color="error"
          icon={<OfflineIcon sx={{ color: "error.main" }} />}
          label={t("commandCenter.metrics.offlineCount", { count: summary.offline })}
        />
      )}
    </Stack>
  );
  const renderStoreHealth = (healthStatus: MonitoringHealthStatus) => {
    if (healthStatus === "neutral") {
      return <Typography color="text.secondary">{t("commandCenter.notAvailable")}</Typography>;
    }

    if (healthStatus === "error") {
      return (
        <Tooltip title={t("commandCenter.errors.errorLoadingDevices")}>
          <CustomerWarningIcon color="error" sx={{ fontSize: 18 }} />
        </Tooltip>
      );
    }

    if (healthStatus === "healthy") {
      return (
        <Tooltip title={t("commandCenter.status.healthy")}>
          <HealthyIcon color="success" sx={{ fontSize: 18 }} />
        </Tooltip>
      );
    }

    if (healthStatus === "toWatch") {
      return (
        <Tooltip title={t("commandCenter.status.toWatch")}>
          <ToWatchIcon color="warning" sx={{ fontSize: 18 }} />
        </Tooltip>
      );
    }

    return (
      <Tooltip title={t("commandCenter.status.unhealthy")}>
        <CustomerWarningIcon color="error" sx={{ fontSize: 18 }} />
      </Tooltip>
    );
  };
  const renderAssociateCount = (count: DeviceCount, associateActivity: AssociateActivity) => (
    <Stack spacing={0.5} alignItems="flex-start">
      {associateActivity === "active" && (
        <Chip
          size="small"
          icon={<AssociateActiveIcon />}
          label={
            count.online > 0
              ? t("commandCenter.status.active")
              : t("commandCenter.status.recentActivity")
          }
          sx={{
            color: "primary.main",
            "& .MuiChip-icon": { color: "primary.main" },
          }}
        />
      )}
      {associateActivity === "noRecent" && (
        <Chip
          size="small"
          icon={<NoAssociateActivityIcon />}
          label={t("commandCenter.status.noRecentActivity")}
        />
      )}
      {renderDeviceCount({ ...count, warning: 0, offline: 0 })}
    </Stack>
  );

  return (
    <Box sx={{ mb: 3 }}>
      <AdminTablePanel
        title={t("commandCenter.sections.deviceHealth")}
        icon={<HealthColumnIcon />}
        actions={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <ExcludedDevicesControl excludedDevices={excludedDevices} t={t} />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={hideHealthyStores}
                  onChange={(event) => setHideHealthyStores(event.target.checked)}
                />
              }
              label={t("commandCenter.controls.hideHealthyStores")}
              sx={{ m: 0 }}
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={hideStoresWithoutDevices}
                  onChange={(event) => setHideStoresWithoutDevices(event.target.checked)}
                />
              }
              label={t("commandCenter.controls.hideStoresWithoutDevices")}
              sx={{ m: 0 }}
            />
          </Stack>
        }
        mobileContent={
          <Box sx={{ minWidth: 0 }}>
            <RecordSortControl
              value={sortColumn}
              direction={sortDirection}
              onSort={handleSort}
              options={[
                { value: "issueRank" as const, label: t("commandCenter.columns.health") },
                ...columns.map(({ key, label }) => ({
                  value: key,
                  label: typeof label === "string" ? label : t("commandCenter.columns.health"),
                })),
              ]}
            />
            <RecordList
              items={displayedItems}
              getItemId={({ store }) => store.id}
              resetKey={JSON.stringify([
                sortColumn,
                sortDirection,
                hideHealthyStores,
                hideStoresWithoutDevices,
                ...displayedItems.map(({ store }) => store.id),
              ])}
              renderTitle={({ store }) => (
                <>
                  <Stack spacing={0.25}>
                    <Typography variant="body2" fontWeight="bold">
                      {store.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {store.customerName}
                    </Typography>
                  </Stack>
                </>
              )}
              renderSummary={({ loading, healthStatus, healthSummary }) => (
                <RecordFields
                  fields={[
                    ...(showStoreHealth
                      ? [
                          {
                            label: t("commandCenter.columns.health"),
                            value: (
                              <>
                                {loading ? (
                                  <Skeleton
                                    variant="circular"
                                    width={18}
                                    height={18}
                                    sx={{ mx: "auto" }}
                                  />
                                ) : (
                                  renderStoreHealth(healthStatus)
                                )}
                              </>
                            ),
                          },
                        ]
                      : []),
                    {
                      label: t("commandCenter.columns.totalDevices"),
                      value: (
                        <>
                          {loading ? (
                            <Skeleton variant="text" width={128} />
                          ) : (
                            renderHealth(healthSummary)
                          )}
                        </>
                      ),
                    },
                  ]}
                />
              )}
              renderDetails={({ loading, health, associateActivity }) => (
                <RecordFields
                  fields={[
                    {
                      label: t("commandCenter.columns.shopperDevices"),
                      value: loading ? (
                        <Skeleton width={32} />
                      ) : (
                        renderDeviceCount(countFor(health?.byType.SHOPPER))
                      ),
                    },
                    {
                      label: t("commandCenter.columns.doorDevices"),
                      value: loading ? (
                        <Skeleton width={32} />
                      ) : (
                        renderDeviceCount(countFor(health?.byType.DOOR))
                      ),
                    },
                    {
                      label: t("commandCenter.columns.associateDevices"),
                      value: (
                        <>
                          {loading ? (
                            <Skeleton variant="text" width={32} />
                          ) : (
                            renderAssociateCount(
                              countFor(health?.byType.ASSOCIATE),
                              associateActivity,
                            )
                          )}
                        </>
                      ),
                    },
                    {
                      label: miscellaneousColumnLabel(t),
                      value: (
                        <>
                          {loading ? (
                            <Skeleton variant="text" width={32} />
                          ) : (
                            renderDeviceCount(miscellaneousDeviceCount(health))
                          )}
                        </>
                      ),
                    },
                  ]}
                />
              )}
              renderActions={
                showStoreHealth
                  ? ({ store }) => (
                      <AdminTableActions
                        actions={[
                          {
                            icon: <ViewIcon />,
                            hint: t("commandCenter.actions.view"),
                            action: () => onSelectStore?.(store),
                          },
                        ]}
                      />
                    )
                  : undefined
              }
              emptyState={<Box />}
            />
          </Box>
        }
      >
        <TableHead>
          <TableRow>
            {columns.map(({ key, label }) => (
              <SortableTableCell
                key={key}
                activeColumn={sortColumn}
                column={key}
                direction={sortDirection}
                onSort={handleSort}
                sx={
                  showStoreHealth && key === "storeHealth"
                    ? storeHealthCellSx
                    : !showStoreHealth && key === "health"
                      ? healthCellSx
                      : undefined
                }
              >
                {label}
              </SortableTableCell>
            ))}
            {showStoreHealth && (
              <TableCell sx={{ textAlign: "end" }}>{t("commandCenter.columns.actions")}</TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {displayedItems.map(
            ({ store, health, healthSummary, healthStatus, associateActivity, loading }) => (
              <TableRow
                key={store.id}
                hover={showStoreHealth}
                role={showStoreHealth ? "button" : undefined}
                tabIndex={showStoreHealth ? 0 : undefined}
                onClick={showStoreHealth ? () => onSelectStore?.(store) : undefined}
                onKeyDown={
                  showStoreHealth
                    ? (event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          onSelectStore?.(store);
                        }
                      }
                    : undefined
                }
                sx={
                  showStoreHealth
                    ? {
                        cursor: "pointer",
                        "&&:hover, &&:hover > .MuiTableCell-root": {
                          backgroundColor: "action.hover",
                        },
                      }
                    : undefined
                }
              >
                {showStoreHealth && (
                  <TableCell sx={storeHealthCellSx}>
                    {loading ? (
                      <Skeleton variant="circular" width={18} height={18} sx={{ mx: "auto" }} />
                    ) : (
                      renderStoreHealth(healthStatus)
                    )}
                  </TableCell>
                )}
                <TableCell sx={{ verticalAlign: "middle" }}>
                  <Stack spacing={0.25}>
                    <Typography variant="body2" fontWeight="bold">
                      {store.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {store.customerName}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell
                  sx={
                    showStoreHealth
                      ? { verticalAlign: "top" }
                      : { ...healthCellSx, verticalAlign: "top" }
                  }
                >
                  {loading ? <Skeleton variant="text" width={128} /> : renderHealth(healthSummary)}
                </TableCell>
                {(["SHOPPER", "DOOR"] as const).map((kind) => (
                  <TableCell key={kind} sx={{ verticalAlign: "top" }}>
                    {loading ? (
                      <Skeleton variant="text" width={32} />
                    ) : (
                      renderDeviceCount(countFor(health?.byType[kind]))
                    )}
                  </TableCell>
                ))}
                <TableCell sx={{ verticalAlign: "top" }}>
                  {loading ? (
                    <Skeleton variant="text" width={32} />
                  ) : (
                    renderAssociateCount(countFor(health?.byType.ASSOCIATE), associateActivity)
                  )}
                </TableCell>
                <TableCell sx={{ verticalAlign: "top" }}>
                  {loading ? (
                    <Skeleton variant="text" width={32} />
                  ) : (
                    renderDeviceCount(miscellaneousDeviceCount(health))
                  )}
                </TableCell>
                {showStoreHealth && (
                  <TableCell sx={{ verticalAlign: "top" }}>
                    <Box onClick={(event) => event.stopPropagation()}>
                      <AdminTableActions
                        actions={[
                          {
                            icon: <ViewIcon />,
                            hint: t("commandCenter.actions.view"),
                            ariaLabel: t("commandCenter.actions.view"),
                            action: () => onSelectStore?.(store),
                          },
                        ]}
                      />
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ),
          )}
        </TableBody>
      </AdminTablePanel>
    </Box>
  );
};
const StoreDeviceDetailTable = ({
  devices,
  store,
  loading,
  error,
  t,
  locale,
  excludedDevices,
}: any) => {
  const navigate = useNavigate();
  const [hideOnlineDevices, setHideOnlineDevices] = React.useState(false);
  const rows = React.useMemo<StoreDeviceDetailTableRow[]>(
    () =>
      devices
        .filter((device: Device) => isEligibleDevice(device, store.timezone))
        .map((device: Device) => {
          const status = getDeviceStatus(device, store.timezone);
          return {
            device,
            status,
            issueRank: status === "online" ? 2 : status === "warning" ? 1 : 0,
          };
        }),
    [devices, store.timezone],
  );
  const { sortColumn, sortDirection, handleSort, sortedItems } = useTableSorting<
    StoreDeviceDetailTableRow,
    StoreDeviceDetailTableColumn | "issueRank"
  >(
    rows,
    (row, column) => {
      if (column === "issueRank") return row.issueRank;
      if (column === "deviceId") return row.device.id;
      if (column === "deviceType") return row.device.type;
      if (column === "name") return row.device.name;
      if (column === "battery") return row.device.batteryPercent;
      if (column === "lastUpdate")
        return row.device.updatedAt ? Date.parse(row.device.updatedAt) : null;
      return row.issueRank;
    },
    "status",
    locale,
  );
  const displayedItems = React.useMemo(
    () => sortedItems.filter((row) => !hideOnlineDevices || row.status !== "online"),
    [hideOnlineDevices, sortedItems],
  );
  const columns: { key: StoreDeviceDetailTableColumn; label: React.ReactNode }[] = [
    {
      key: "status",
      label: (
        <Tooltip title={t("commandCenter.columns.status")}>
          <AssociateWifiIcon sx={{ fontSize: 18 }} />
        </Tooltip>
      ),
    },
    { key: "deviceType", label: t("commandCenter.columns.deviceType") },
    { key: "deviceId", label: t("commandCenter.columns.deviceId") },
    { key: "name", label: t("commandCenter.columns.name") },
    { key: "battery", label: t("commandCenter.columns.battery") },
    { key: "lastUpdate", label: t("commandCenter.columns.lastUpdate") },
  ];
  const statusCellSx = {
    borderInlineEnd: "1px solid",
    borderColor: "divider",
    borderTop: 0,
    borderBottom: 0,
    ...healthIconTableCellSx,
    ...healthIconTableHeaderCellSx,
  };

  return (
    <Box sx={{ mb: 3 }}>
      <AdminTablePanel
        title={t("commandCenter.sections.deviceDetails")}
        icon={<DevicesIcon />}
        actions={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <ExcludedDevicesControl excludedDevices={excludedDevices} t={t} />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={hideOnlineDevices}
                  onChange={(event) => setHideOnlineDevices(event.target.checked)}
                />
              }
              label={t("commandCenter.controls.hideOnlineDevices")}
              sx={{ m: 0 }}
            />
          </Stack>
        }

        mobileContent={
          <Box
            sx={{
              minWidth: 0,
              "& .MuiTypography-root": {
                maxWidth: "100%",
                whiteSpace: "normal",
                overflowWrap: "anywhere",
              },
            }}
          >
            <RecordSortControl
              value={sortColumn}
              direction={sortDirection}
              onSort={handleSort}
              options={[
                { value: "status" as const, label: t("commandCenter.columns.status") },
                { value: "deviceType" as const, label: t("commandCenter.columns.deviceType") },
                { value: "deviceId" as const, label: t("commandCenter.columns.deviceId") },
                { value: "name" as const, label: t("commandCenter.columns.name") },
                { value: "battery" as const, label: t("commandCenter.columns.battery") },
                { value: "lastUpdate" as const, label: t("commandCenter.columns.lastUpdate") },
              ]}
            />
            <RecordList
              items={displayedItems}
              getItemId={(row) => row.device.id}
              loading={false}
              resetKey={JSON.stringify([
                sortColumn,
                sortDirection,
                ...displayedItems.map((row) => row.device.id),
              ])}
              renderTitle={(row) => {
                return <>{row.device.name}</>;
              }}
              renderSummary={(row) => {
                const battery = row.device.batteryPercent;
                const batteryDisplay = getDeviceBatteryDisplay(row.device);
                return (
                  <RecordFields
                    fields={[
                      {
                        label: <>{t("commandCenter.columns.status")}</>,
                        value: (
                          <>
                            {row.status === "online" ? (
                              <Tooltip title={t("commandCenter.status.online")}>
                                <AssociateWifiIcon color="success" sx={{ fontSize: 18 }} />
                              </Tooltip>
                            ) : row.status === "warning" ? (
                              <Tooltip title={t("commandCenter.status.stale")}>
                                <StaleUpdateIcon color="warning" sx={{ fontSize: 18 }} />
                              </Tooltip>
                            ) : (
                              <Tooltip title={t("commandCenter.status.offline")}>
                                <OfflineIcon color="error" sx={{ fontSize: 18 }} />
                              </Tooltip>
                            )}
                          </>
                        ),
                      },
                      {
                        label: <>{t("commandCenter.columns.battery")}</>,
                        value: (
                          <>
                            {batteryDisplay && battery !== undefined ? (
                              <DeviceBatteryChip
                                device={row.device}
                                locale={locale}
                                wallPowerLabel={t("commandCenter.status.wallPower")}
                              />
                            ) : (
                              t("commandCenter.notAvailable")
                            )}
                          </>
                        ),
                      },
                    ]}
                  />
                );
              }}
              renderDetails={(row) => {
                const deviceKind = kinds.find((kind) => kind === row.device.type.toUpperCase());
                const deviceType = deviceKind ? deviceTypeConfig[deviceKind] : null;

                return (
                  <RecordFields
                    fields={[
                      {
                        label: <>{t("commandCenter.columns.deviceType")}</>,
                        value: (
                          <>
                            {deviceType ? (
                              <Stack direction="row" spacing={0.75} alignItems="center">
                                <Box
                                  component="img"
                                  src={deviceType.iconSrc}
                                  alt=""
                                  sx={{
                                    width: 20,
                                    height: 20,
                                    objectFit: "cover",
                                    borderRadius: "50%",
                                    overflow: "hidden",
                                  }}
                                />
                                <Typography variant="body2" fontWeight="bold">
                                  {t(deviceType.labelKey)}
                                </Typography>
                              </Stack>
                            ) : (
                              t("commandCenter.notAvailable")
                            )}
                          </>
                        ),
                      },
                      {
                        label: <>{t("commandCenter.columns.deviceId")}</>,
                        value: <>{row.device.id}</>,
                      },
                      {
                        label: <>{t("commandCenter.columns.lastUpdate")}</>,
                        value: (
                          <>
                            {row.device.updatedAt ? (
                              <Stack spacing={0.25}>
                                <Typography variant="body2">
                                  {formatStoreDateTimeWithTimezone(
                                    row.device.updatedAt,
                                    getBrowserTimezone(),
                                    locale,
                                  )}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatRelativeStoreTime(
                                    row.device.updatedAt,
                                    getBrowserTimezone(),
                                    locale,
                                  )}
                                </Typography>
                              </Stack>
                            ) : (
                              t("commandCenter.notAvailable")
                            )}
                          </>
                        ),
                      },
                    ]}
                  />
                );
              }}
              renderActions={(row) => {
                const viewDevice = () => {
                  void navigate(`/admin/hardware/devices/history/${row.device.id}`);
                };
                return (
                  <AdminTableActions
                    actions={[
                      {
                        icon: <ViewIcon />,
                        hint: t("commandCenter.actions.view"),
                        ariaLabel: t("commandCenter.actions.view"),
                        action: viewDevice,
                      },
                    ]}
                  />
                );
              }}
              emptyState={
                <AdminEmptyState
                  icon={<DevicesIcon sx={{ fontSize: 40 }} />}
                  text={
                    hideOnlineDevices
                      ? t("commandCenter.empty.noDevicesMatchingFilters")
                      : t("commandCenter.empty.noDevicesFound", { message: "" })
                  }
                />
              }
            />
          </Box>
        }
      >
        <TableHead>
          <TableRow>
            {columns.map(({ key, label }) => (
              <SortableTableCell
                key={key}
                activeColumn={sortColumn}
                column={key}
                direction={sortDirection}
                onSort={handleSort}
                sx={key === "status" ? statusCellSx : undefined}
              >
                {label}
              </SortableTableCell>
            ))}
            <TableCell sx={{ textAlign: "end" }}>{t("commandCenter.columns.actions")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {error ? (
            <TableRow>
              <TableCell colSpan={columns.length + 1} align="center">
                <Alert severity="error">{t("commandCenter.errors.errorLoadingDevices")}</Alert>
              </TableCell>
            </TableRow>
          ) : loading ? (
            <AdminLoadingRow
              colSpan={columns.length + 1}
              label={t("commandCenter.empty.loadingDeviceInformation")}
            />
          ) : displayedItems.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length + 1} align="center">
                <AdminEmptyState
                  icon={<DevicesIcon sx={{ fontSize: 40 }} />}
                  text={
                    hideOnlineDevices
                      ? t("commandCenter.empty.noDevicesMatchingFilters")
                      : t("commandCenter.empty.noDevicesFound", { message: "" })
                  }
                />
              </TableCell>
            </TableRow>
          ) : (
            displayedItems.map((row) => {
              const battery = row.device.batteryPercent;
              const batteryDisplay = getDeviceBatteryDisplay(row.device);
              const deviceKind = kinds.find((kind) => kind === row.device.type.toUpperCase());
              const deviceType = deviceKind ? deviceTypeConfig[deviceKind] : null;
              const viewDevice = () => {
                void navigate(`/admin/hardware/devices/history/${row.device.id}`);
              };
              return (
                <TableRow
                  key={row.device.id}
                  hover
                  role="button"
                  tabIndex={0}
                  onClick={viewDevice}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      viewDevice();
                    }
                  }}
                  sx={{
                    cursor: "pointer",
                    "&&:hover, &&:hover > .MuiTableCell-root": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <TableCell sx={statusCellSx}>
                    {row.status === "online" ? (
                      <Tooltip title={t("commandCenter.status.online")}>
                        <AssociateWifiIcon color="success" sx={{ fontSize: 18 }} />
                      </Tooltip>
                    ) : row.status === "warning" ? (
                      <Tooltip title={t("commandCenter.status.stale")}>
                        <StaleUpdateIcon color="warning" sx={{ fontSize: 18 }} />
                      </Tooltip>
                    ) : (
                      <Tooltip title={t("commandCenter.status.offline")}>
                        <OfflineIcon color="error" sx={{ fontSize: 18 }} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    {deviceType ? (
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        <Box
                          component="img"
                          src={deviceType.iconSrc}
                          alt=""
                          sx={{
                            width: 20,
                            height: 20,
                            objectFit: "cover",
                            borderRadius: "50%",
                            overflow: "hidden",
                          }}
                        />
                        <Typography variant="body2" fontWeight="bold">
                          {t(deviceType.labelKey)}
                        </Typography>
                      </Stack>
                    ) : (
                      t("commandCenter.notAvailable")
                    )}
                  </TableCell>
                  <TableCell>{row.device.id}</TableCell>
                  <TableCell>{row.device.name}</TableCell>
                  <TableCell>
                    {batteryDisplay && battery !== undefined ? (
                      <DeviceBatteryChip
                        device={row.device}
                        locale={locale}
                        wallPowerLabel={t("commandCenter.status.wallPower")}
                      />
                    ) : (
                      t("commandCenter.notAvailable")
                    )}
                  </TableCell>
                  <TableCell>
                    {row.device.updatedAt ? (
                      <Stack spacing={0.25}>
                        <Typography variant="body2">
                          {formatStoreDateTimeWithTimezone(
                            row.device.updatedAt,
                            getBrowserTimezone(),
                            locale,
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatRelativeStoreTime(
                            row.device.updatedAt,
                            getBrowserTimezone(),
                            locale,
                          )}
                        </Typography>
                      </Stack>
                    ) : (
                      t("commandCenter.notAvailable")
                    )}
                  </TableCell>
                  <TableCell>
                    <Box onClick={(event) => event.stopPropagation()}>
                      <AdminTableActions
                        actions={[
                          {
                            icon: <ViewIcon />,
                            hint: t("commandCenter.actions.view"),
                            ariaLabel: t("commandCenter.actions.view"),
                            action: viewDevice,
                          },
                        ]}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </AdminTablePanel>
    </Box>
  );
};

const CustomerHealthTableRow = React.memo(function CustomerHealthTableRow({
  row,
  branding,
  onSelect,
  t,
}: CustomerRowProps) {
  const trailingCellDividerSx = { borderInlineEnd: "1px solid", borderColor: "divider" };
  const healthCellSx = { ...trailingCellDividerSx, borderTop: 0, borderBottom: 0 };
  const customerHealthCellSx = {
    ...healthCellSx,
    ...iconOnlyTableCellSx,
    ...healthIconTableCellSx,
  };
  const renderDeviceCount = (count: DeviceCount) => (
    <Stack spacing={0.5} alignItems="flex-start">
      {count.online > 0 && (
        <Chip
          size="small"
          color="success"
          icon={<HealthyIcon />}
          label={t("commandCenter.metrics.onlineCount", { count: count.online })}
        />
      )}
      {count.lowBattery > 0 && (
        <Chip
          size="small"
          color="warning"
          icon={<BatteryAlert />}
          label={t("commandCenter.status.lowBattery", { count: count.lowBattery })}
        />
      )}
      {count.warning > 0 && (
        <Chip
          size="small"
          color="warning"
          icon={<StaleUpdateIcon />}
          label={t("commandCenter.status.staleUpdates", { count: count.warning })}
        />
      )}
      {count.offline > 0 && (
        <Chip
          size="small"
          color="error"
          icon={<OfflineIcon sx={{ color: "error.main" }} />}
          label={t("commandCenter.metrics.offlineCount", { count: count.offline })}
        />
      )}
    </Stack>
  );
  const renderHealth = (health: CustomerHealthSummary) => (
    <Stack spacing={0.5} alignItems="flex-start">
      {health.online > 0 && (
        <Chip
          size="small"
          color="success"
          icon={<HealthyIcon />}
          label={t("commandCenter.metrics.onlineCount", { count: health.online })}
        />
      )}
      {health.lowBattery > 0 && (
        <Chip
          size="small"
          color="warning"
          icon={<BatteryAlert />}
          label={t("commandCenter.status.lowBattery", { count: health.lowBattery })}
        />
      )}
      {health.warning > 0 && (
        <Chip
          size="small"
          color="warning"
          icon={<StaleUpdateIcon />}
          label={t("commandCenter.status.staleUpdates", { count: health.warning })}
        />
      )}
      {health.offline > 0 && (
        <Chip
          size="small"
          color="error"
          icon={<OfflineIcon sx={{ color: "error.main" }} />}
          label={t("commandCenter.metrics.offlineCount", { count: health.offline })}
        />
      )}
    </Stack>
  );
  const renderCustomerHealth = (healthStatus: Exclude<MonitoringHealthStatus, "neutral">) => {
    if (healthStatus === "error") {
      return (
        <Tooltip title={t("commandCenter.errors.errorLoadingDevices")}>
          <CustomerWarningIcon color="error" sx={{ fontSize: 18 }} />
        </Tooltip>
      );
    }

    if (healthStatus === "healthy") {
      return (
        <Tooltip title={t("commandCenter.status.healthy")}>
          <HealthyIcon color="success" sx={{ fontSize: 18 }} />
        </Tooltip>
      );
    }

    if (healthStatus === "toWatch") {
      return (
        <Tooltip title={t("commandCenter.status.customerHasStoreToWatch")}>
          <ToWatchIcon color="warning" sx={{ fontSize: 18 }} />
        </Tooltip>
      );
    }

    return (
      <Tooltip title={t("commandCenter.status.customerHasUnhealthyStore")}>
        <CustomerWarningIcon color="error" sx={{ fontSize: 18 }} />
      </Tooltip>
    );
  };
  const renderAssociateCount = (count: DeviceCount, activity: AssociateActivity) => (
    <Stack spacing={0.5} alignItems="flex-start">
      {activity === "active" && (
        <Chip
          size="small"
          icon={<AssociateActiveIcon />}
          label={
            count.online > 0
              ? t("commandCenter.status.active")
              : t("commandCenter.status.recentActivity")
          }
          sx={{
            color: "primary.main",
            "& .MuiChip-icon": { color: "primary.main" },
          }}
        />
      )}
      {activity === "noRecent" && (
        <Chip
          size="small"
          icon={<NoAssociateActivityIcon />}
          label={t("commandCenter.status.noRecentActivity")}
        />
      )}
      {renderDeviceCount({ ...count, warning: 0, offline: 0 })}
    </Stack>
  );

  const recordTheme = useTheme();
  const showMobileRecords = useMediaQuery(recordTheme.breakpoints.down("md"));
  if (showMobileRecords)
    return (
      <RecordItem
        title={
          <>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Avatar
                src={branding.avatar}
                sx={{ width: 20, height: 20, border: `1px solid ${branding.color}` }}
              />
              <Typography variant="body2" fontWeight="bold">
                {row.customer}
              </Typography>
            </Stack>
          </>
        }
        summary={
          <RecordFields
            fields={[
              {
                label: t("commandCenter.columns.health"),
                value: (
                  <>
                    {row.loading ? (
                      <Skeleton variant="circular" width={18} height={18} sx={{ mx: "auto" }} />
                    ) : (
                      renderCustomerHealth(row.healthStatus)
                    )}
                  </>
                ),
              },
              {
                label: t("commandCenter.columns.totalDevices"),
                value: (
                  <>
                    {row.loading ? (
                      <Skeleton variant="text" width={128} />
                    ) : (
                      renderHealth(row.health)
                    )}
                  </>
                ),
              },
            ]}
          />
        }
        details={
          <RecordFields
            fields={[
              {
                label: t("commandCenter.columns.shopperDevices"),
                value: row.loading ? <Skeleton width={32} /> : renderDeviceCount(row.shopper),
              },
              {
                label: t("commandCenter.columns.doorDevices"),
                value: row.loading ? <Skeleton width={32} /> : renderDeviceCount(row.door),
              },
              {
                label: t("commandCenter.columns.associateDevices"),
                value: row.loading ? (
                  <Skeleton width={32} />
                ) : (
                  renderAssociateCount(row.associate, row.associateActivity)
                ),
              },
              {
                label: miscellaneousColumnLabel(t),
                value: row.loading ? <Skeleton width={32} /> : renderDeviceCount(row.assist),
              },
            ]}
          />
        }
        actions={
          <AdminTableActions
            actions={[
              {
                icon: <ViewIcon />,
                hint: t("commandCenter.actions.view"),
                action: () => onSelect(row.customerRecord),
              },
            ]}
          />
        }
      />
    );
  return (
    <TableRow
      hover
      role="button"
      tabIndex={0}
      onClick={() => onSelect(row.customerRecord)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(row.customerRecord);
        }
      }}
      sx={{ cursor: "pointer" }}
    >
      <TableCell sx={{ ...customerHealthCellSx, verticalAlign: "middle" }}>
        {row.loading ? (
          <Skeleton variant="circular" width={18} height={18} sx={{ mx: "auto" }} />
        ) : (
          renderCustomerHealth(row.healthStatus)
        )}
      </TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Avatar
            src={branding.avatar}
            sx={{ width: 20, height: 20, border: `1px solid ${branding.color}` }}
          />
          <Typography variant="body2" fontWeight="bold">
            {row.customer}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell sx={{ ...healthCellSx, verticalAlign: "top" }}>
        {row.loading ? <Skeleton variant="text" width={128} /> : renderHealth(row.health)}
      </TableCell>
      {[row.shopper, row.door].map((count, countIndex) => (
        <TableCell key={countIndex} sx={{ verticalAlign: "top" }}>
          {row.loading ? <Skeleton variant="text" width={32} /> : renderDeviceCount(count)}
        </TableCell>
      ))}
      <TableCell sx={{ verticalAlign: "top" }}>
        {row.loading ? (
          <Skeleton variant="text" width={32} />
        ) : (
          renderAssociateCount(row.associate, row.associateActivity)
        )}
      </TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>
        {row.loading ? <Skeleton variant="text" width={32} /> : renderDeviceCount(row.assist)}
      </TableCell>
      <TableCell sx={{ verticalAlign: "top" }}>
        <Box onClick={(event) => event.stopPropagation()}>
          <AdminTableActions
            actions={[
              {
                icon: <ViewIcon />,
                hint: t("commandCenter.actions.view"),
                ariaLabel: t("commandCenter.actions.view"),
                action: () => onSelect(row.customerRecord),
              },
            ]}
          />
        </Box>
      </TableCell>
    </TableRow>
  );
}, equalCustomerRowProps);

const CustomerDeviceTable = ({
  rows,
  t,
  locale,
  onSelectCustomer,
  excludedDevices,
}: {
  rows: CustomerDeviceTableRow[];
  t: TFunction;
  locale: string;
  onSelectCustomer: (customer: Customer) => void;
  excludedDevices: ExcludedDeviceRow[];
}) => {
  const { getStoreBranding } = useStoreBranding();
  const { sortColumn, sortDirection, handleSort, sortedItems } = useTableSorting<
    CustomerDeviceTableRow,
    CustomerDeviceTableColumn | "issueRank"
  >(
    rows,
    (row, column) => {
      if (column === "customerHealth") {
        return row.healthStatus === "error"
          ? 0
          : row.healthStatus === "unhealthy"
            ? 1
            : row.healthStatus === "toWatch"
              ? 2
              : 3;
      }
      const value = row[column];
      if (typeof value !== "object") return value;
      return "issueCount" in value ? value.issueCount : value.total;
    },
    "customerHealth",
    locale,
  );
  const sectionedItems = React.useMemo(
    () => [
      ...sortedItems.filter((row) => !isNaCraveCustomer(row.customerId)),
      ...sortedItems.filter((row) => isNaCraveCustomer(row.customerId)),
    ],
    [sortedItems],
  );
  const columns: { key: CustomerDeviceTableColumn; label: React.ReactNode }[] = [
    {
      key: "customerHealth",
      label: (
        <Tooltip title={t("commandCenter.columns.health")}>
          <HealthColumnIcon sx={{ fontSize: 18 }} />
        </Tooltip>
      ),
    },
    { key: "customer", label: t("commandCenter.columns.customer") },
    { key: "health", label: t("commandCenter.columns.totalDevices") },
    { key: "shopper", label: t("commandCenter.columns.shopperDevices") },
    { key: "door", label: t("commandCenter.columns.doorDevices") },
    { key: "associate", label: t("commandCenter.columns.associateDevices") },
    { key: "assist", label: miscellaneousColumnLabel(t) },
  ];
  const trailingCellDividerSx = { borderInlineEnd: "1px solid", borderColor: "divider" };
  const healthCellSx = { ...trailingCellDividerSx, borderTop: 0, borderBottom: 0 };
  const customerHealthCellSx = {
    ...healthCellSx,
    ...iconOnlyTableCellSx,
    ...healthIconTableCellSx,
  };
  const customerHealthHeaderCellSx = {
    ...customerHealthCellSx,
    ...iconOnlyTableHeaderCellSx,
    ...healthIconTableHeaderCellSx,
  };

  return (
    <Box sx={{ mb: 3 }}>
      <AdminTablePanel
        title={t("commandCenter.sections.deviceHealth")}
        icon={<HealthColumnIcon />}
        actions={<ExcludedDevicesControl excludedDevices={excludedDevices} t={t} />}
        mobileContent={
          <Box sx={{ minWidth: 0 }}>
            <RecordSortControl
              value={sortColumn}
              direction={sortDirection}
              onSort={handleSort}
              options={columns.map(({ key, label }) => ({
                value: key,
                label: typeof label === "string" ? label : t("commandCenter.columns.health"),
              }))}
            />
            <Box
              component="ul"
              key={sortColumn + sortDirection}
              sx={{ listStyle: "none", p: 0, m: 0 }}
            >
              {sectionedItems.map((row, index) => {
                const previousRow = sectionedItems[index - 1];
                const startsCraveCustomers =
                  isNaCraveCustomer(row.customerId) &&
                  (!previousRow || !isNaCraveCustomer(previousRow.customerId));
                return (
                  <React.Fragment key={row.customerId}>
                    {startsCraveCustomers && (
                      <Box
                        component="li"
                        role="separator"
                        sx={{ borderTop: 1, borderColor: "divider" }}
                      />
                    )}
                    <CustomerHealthTableRow
                      row={row}
                      branding={getStoreBranding(row.customerId)}
                      onSelect={onSelectCustomer}
                      t={t}
                      locale={locale}
                    />
                  </React.Fragment>
                );
              })}
            </Box>
          </Box>
        }
      >
        <TableHead>
          <TableRow>
            {columns.map(({ key, label }) => (
              <SortableTableCell
                key={key}
                activeColumn={sortColumn}
                column={key}
                direction={sortDirection}
                onSort={handleSort}
                sx={
                  key === "customerHealth"
                    ? customerHealthHeaderCellSx
                    : key === "health"
                      ? healthCellSx
                      : undefined
                }
              >
                {label}
              </SortableTableCell>
            ))}
            <TableCell sx={{ textAlign: "end" }}>{t("commandCenter.columns.actions")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sectionedItems.map((row, index) => {
            const previousRow = sectionedItems[index - 1];
            const startsCraveCustomers =
              isNaCraveCustomer(row.customerId) &&
              (!previousRow || !isNaCraveCustomer(previousRow.customerId));
            const branding = getStoreBranding(row.customerId);
            return (
              <React.Fragment key={row.customerId}>
                {startsCraveCustomers && (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length + 1}
                      sx={{ p: 0, borderBottom: "1px solid", borderColor: "divider" }}
                    />
                  </TableRow>
                )}
                <CustomerHealthTableRow
                  row={row}
                  branding={branding}
                  onSelect={onSelectCustomer}
                  t={t}
                  locale={locale}
                />
              </React.Fragment>
            );
          })}
        </TableBody>
      </AdminTablePanel>
    </Box>
  );
};
export default CommandCenterPage;
